import { Box } from '@chakra-ui/react';
import React, { UIEvent, useCallback, useRef, useState } from 'react';
import { ChevronLeftIcon } from 'ui-components/dist/ChevronLeftIcon';
import { ChevronRightIcon } from 'ui-components/dist/ChevronRightIcon';
import IconButton from '~/components/IconButton';
import { CarouselItem } from '~/screens/MyAgency/components/StackView/CarouselItem';
import { ISearchContentResult } from '~/searchApi';
import { ICreatorAgencyContentSelected } from '~/types';

interface Props {
  contents: (ISearchContentResult | ICreatorAgencyContentSelected)[];
  showPlatformIcon?: boolean;
}

const CustomNextButton = ({
  next,
  isVisible,
}: {
  next?: () => void;
  isVisible: boolean;
}) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      next?.();
    },
    [next],
  );

  return (
    <>
      {isVisible ? (
        <IconButton
          icon={<ChevronRightIcon boxSize={4} color="primary.700" />}
          zIndex={1}
          position="absolute"
          top="40%"
          right={5}
          width={6}
          height={6}
          borderRadius="full"
          color="primary.700"
          backgroundColor="primary.50"
          size="lg"
          aria-label="Arrow Right"
          onClick={handleClick}
        />
      ) : null}
    </>
  );
};

const CustomPrevButton = ({
  prev,
  isVisible,
}: {
  prev?: () => void;
  isVisible: boolean;
}) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      prev?.();
    },
    [prev],
  );
  return (
    <>
      {isVisible ? (
        <IconButton
          icon={<ChevronLeftIcon boxSize={4} color="primary.700" />}
          zIndex={1}
          position="absolute"
          top="40%"
          left={5}
          width={6}
          height={6}
          borderRadius="full"
          color="primary.700"
          backgroundColor="primary.50"
          size="lg"
          aria-label="Arrow Left"
          onClick={handleClick}
        />
      ) : null}
    </>
  );
};

export const Carousel: React.FC<Props> = ({ contents, showPlatformIcon }) => {
  const ref = useRef<HTMLDivElement>(null!);
  const [scrollPosition, setScrollPosition] = useState<
    'start' | 'middle' | 'end'
  >('start');
  const [hasHorizontalScrollbar, setHasHorizontalScrollbar] =
    useState<boolean>(false);

  const next = useCallback(() => {
    const nextPosition = ref.current.scrollLeft + 200;
    ref.current?.scrollTo({ top: 0, left: nextPosition, behavior: 'smooth' });
  }, [ref]);

  const prev = useCallback(() => {
    const prevPosition = ref.current.scrollLeft - 200;
    ref.current?.scrollTo({ top: 0, left: prevPosition, behavior: 'smooth' });
  }, [ref]);

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const { scrollWidth, scrollLeft, clientWidth } =
        e.target as HTMLInputElement;

      const result = scrollLeft + clientWidth;

      if (result === scrollWidth) {
        setScrollPosition('end');
        return;
      }

      if (scrollLeft === 0) {
        setScrollPosition('start');
        return;
      }

      setScrollPosition('middle');
    },
    [setScrollPosition],
  );

  const handleOnLoad = useCallback(() => {
    const { scrollWidth, clientWidth } = ref.current;

    if (scrollWidth <= clientWidth) setHasHorizontalScrollbar(true);
  }, [ref, setHasHorizontalScrollbar]);

  return (
    <Box
      position="relative"
      display="block"
      width="100%"
      data-testid="carousel"
    >
      <CustomPrevButton prev={prev} isVisible={scrollPosition !== 'start'} />
      <Box
        ref={ref}
        position="relative"
        display="-webkit-box"
        overflowX="auto"
        onScroll={handleScroll}
        onLoad={handleOnLoad}
        css={{
          '::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {contents.map((content) => (
          <CarouselItem
            key={content.contentId}
            content={content}
            showPlatformIcon={showPlatformIcon}
          />
        ))}
      </Box>
      <CustomNextButton
        next={next}
        isVisible={scrollPosition !== 'end' && !hasHorizontalScrollbar}
      />
    </Box>
  );
};
